import i18n from 'i18next';

import { QuestionType, RESULTS_SUMMARY } from '~/data/surveys';
import { capitalizeFirstLetter } from '~/utils/helper';

import {
  BarData,
  C,
  CColor,
  CLabel,
  DonutData,
  LikertData,
  LikertLegendData,
  ScaleInterval,
  ServerChoiceData,
  ServerResultsData,
  ServerScaleData,
  ServerSummaryData,
  ServerSurveyResults,
  ServerYesNoData,
  SurveyResultsData,
  SurveyResultsObject,
  SurveySummaryData,
} from '../../types';

const getChartColor = (
  idx: number,
  type?: RESULTS_SUMMARY.AGE | QuestionType,
) => {
  let colors: string[];

  switch (type) {
    case RESULTS_SUMMARY.AGE:
      colors = [
        'hsl(195, 96%, 43%)',
        'hsl(94, 73%, 84%)',
        'hsl(195, 93%, 88%)',
        'hsl(49, 100%, 50%)',
        'hsl(216, 22%, 86%)',
      ];
      break;
    case QuestionType.SCALE:
      colors = [
        'hsl(49, 100%, 50%)',
        'hsl(45, 100%, 80%)',
        'hsl(216, 22%, 87%)',
        'hsl(195, 96%, 89%)',
        'hsl(195, 95%, 43%)',
      ];
      break;
    case QuestionType.MULTIPLE_CHOICE:
    case QuestionType.SINGLE_CHOICE:
      colors = ['hsl(195, 96%, 89%)'];
      break;
    default:
      colors = [
        'hsl(195, 96%, 43%)',
        'hsl(49, 100%, 50%)',
        'hsl(94, 73%, 84%)',
        'hsl(216, 22%, 86%)',
      ];
      break;
  }

  return colors[idx];
};

const getDonutData = (
  k: string,
  v: number,
  i: number,
  total: number,
  age?: boolean,
): DonutData => {
  let label = '';
  if (k === 'yes' || k === 'no') {
    label = i18n.t(`la${capitalizeFirstLetter(k.toLowerCase())}`);
  } else if (!age) {
    label = i18n.t(`label_question_${k}`);
  } else {
    label = k;
  }

  return Object.assign(
    !age
      ? {
          percentageLabel: `${v} ${((v * 100) / total).toFixed(1).toString()}%`,
        }
      : {},
    {
      id: label,
      label,
      value: v,
      color: getChartColor(i, age ? RESULTS_SUMMARY.AGE : undefined),
    },
  );
};

const getBarData = (arr: ServerChoiceData[], type: QuestionType): BarData[] => {
  const barDataArr = arr.map((bd) => {
    return {
      label: bd.optionText,
      value: bd.count,
      color: getChartColor(0, type),
      id: bd.optionId,
    };
  });

  return barDataArr.sort((a, b) => b.value - a.value);
};

const getScaleData = (scO: ServerScaleData, question: string): LikertData => {
  const so: LikertData = { question };

  Object.keys(scO).forEach((key, i) => {
    const kId = `c${key}`;
    so[kId as C] = scO[key as ScaleInterval];
    so[`${kId}Label` as CLabel] = i18n.t(`label_likert_scale_${key}`);
    so[`${kId}Color` as CColor] = getChartColor(i, QuestionType.SCALE);
  });

  return so;
};

const getScaleLegend = (
  ans: ServerScaleData,
  sData: LikertData,
): LikertLegendData[] => {
  const sLegend = [] as LikertLegendData[];

  Object.keys(ans).forEach((k) => {
    if (parseInt(k, 10) % 2 !== 0) {
      sLegend.push({
        color: sData[`c${k}Color` as CColor] as string,
        label: sData[`c${k}Label` as CLabel] as string,
      });
    }
  });

  return sLegend;
};

export const generateResults = (r: ServerSurveyResults) => {
  const resObj: SurveyResultsObject = {
    id: r.surveyId,
    name: r.name,
    summary: [] as SurveySummaryData[],
    results: [] as SurveyResultsData[],
    showResults: false,
  };

  let totalAnswered = 0;
  if (r.hasOwnProperty('summary')) {
    r.summary.forEach((rs: ServerSummaryData) => {
      const rsO: SurveySummaryData = {
        type: rs.type,
        title: i18n.t(`title_chart_${rs.type}`),
        data: [],
        total: undefined,
      };
      let total = 0;

      if (
        rs.type === RESULTS_SUMMARY.ACTIVITY &&
        typeof rs.didAnswer !== 'undefined' &&
        typeof rs.didNotAnswer !== 'undefined'
      ) {
        total = rs.didAnswer + rs.didNotAnswer;
        totalAnswered = rs.didAnswer;
        rsO.title = `${i18n.t(`title_chart_${rs.type}`)} ${Math.round(
          (totalAnswered * 100) / total,
        )}%`;
        resObj.showResults = rs.didAnswer > 0;
      }

      if (rs.type === RESULTS_SUMMARY.GENDER) {
        total = totalAnswered;
      }

      Object.keys(rs).forEach((key, i) => {
        if (key !== 'type') {
          rsO.data.push(
            getDonutData(
              key,
              rs[key as keyof ServerSummaryData] as number,
              i,
              total,
              rs.type === RESULTS_SUMMARY.AGE,
            ),
          );
          rsO.total = rs.type !== RESULTS_SUMMARY.AGE ? total : undefined;
        }
      });

      resObj.summary.push(rsO);
    });
  }

  if (r.hasOwnProperty('results')) {
    r.results.forEach((rr: ServerResultsData) => {
      const { questionId, questionType, questionText, results, index } = rr;

      const rrO: SurveyResultsData = {
        id: questionId,
        type: questionType,
        title: questionText,
        answers: [],
        legend: undefined,
        total: totalAnswered,
        index,
      };

      if (questionType === QuestionType.FREE_TEXT) {
        rrO.answers = [...(results as string[])];
      }

      if (
        questionType === QuestionType.MULTIPLE_CHOICE ||
        questionType === QuestionType.SINGLE_CHOICE
      ) {
        rrO.answers = getBarData(results as ServerChoiceData[], questionType);
      }

      if (questionType === QuestionType.YES_NO) {
        Object.keys(rr.results).forEach((key, i) => {
          rrO.answers.push(
            getDonutData(
              key,
              (results as ServerYesNoData)[key as keyof ServerYesNoData],
              i,
              totalAnswered,
            ),
          );
        });
      }

      if (questionType === QuestionType.SCALE) {
        const scaleData = getScaleData(
          rr.results as ServerScaleData,
          rr.questionText,
        );
        rrO.answers.push(scaleData);
        rrO.legend = getScaleLegend(rr.results as ServerScaleData, scaleData);
      }

      resObj.results.push(rrO);
    });

    const res = [...resObj.results]
      .filter((q) => q.type !== QuestionType.FREE_TEXT)
      .sort((a, b) => a.index - b.index);
    const comm = [...resObj.results]
      .filter((q) => q.type === QuestionType.FREE_TEXT)
      .sort((a, b) => a.index - b.index);
    resObj.results = res.concat(comm);
  }

  return resObj;
};
